import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LiliButton } from '@lili/lili-frontend-common';
import Heading from '../../../../generic-components/Heading';
import { LiliPlanType, STYLES } from '../../constants';
import { ILiliPlan } from '../../types';
import { FlowContext } from '../../index';
import { LiliPlanHighlightSectionView } from './PlanHighlightSection';
import { PlanInfoView } from './PlanInfoView';
import { PremiumPlanInfoView } from './PremiumPlanInfoView';
import { GENERIC_FILE_UPLOAD_TYPES } from '../../../../types';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';
import { ReactComponent as FailedIcon } from '../../../../assets/icons/svg/stamp-icons/ic-stamp-failed.svg';
import { useUploadGenericFile } from '../../../../hooks/utilities/useUploadGenericFile';
import { featureAccess, LimitedFeatureType } from '../../../../services/appAccess.service';
import { FEATURE_FLAG } from '../../../../constants/general';
import { FeatureAccess } from '../../../../pages/AccountSettings/components/rolesAndPermissions/PermissionsList';

interface IProps {
  plan: ILiliPlan;
  onUpgradeClick: (plan: ILiliPlan) => void;
  isAnnual: boolean;
}

export const PlanView = ({ plan, onUpgradeClick, isAnnual }: IProps) => {
  const context = useContext(FlowContext);
  const scrollableContentContainer = useRef<HTMLDivElement | null>(null);
  const scrollableContent = useRef<HTMLDivElement | null>(null);
  const planUpgradePermission = featureAccess(FEATURE_FLAG.PLAN_UPGRADE, LimitedFeatureType.UpgradePlan);
  const [shouldShowAuthUserUpgradeDisclaimer, setShouldShowAuthUserUpgradeDisclaimer] = useState<boolean>(false);
  const { t } = useTranslation();
  const { mutateAsync: postUploadSnapShot, isLoading: isLoadingUploadFile } = useUploadGenericFile();
  const { sections, badge } = plan;
  const isPremium = plan.type === LiliPlanType.PREMIUM;

  const handlePlanUpgradeClick = async () => {
    await postUploadSnapShot({
      fileUsageType: GENERIC_FILE_UPLOAD_TYPES.PLAN_UPGRADE_SUCCESS,
      imageName: `${Date.now()}`,
    });
    if (planUpgradePermission !== FeatureAccess.READ_WRITE) {
      setShouldShowAuthUserUpgradeDisclaimer(true);
    } else {
      onUpgradeClick(plan);
    }
  };

  const planBadge = (
    <div
      className={`items-center h-[30px] w-full  rounded-t-sm  ${badge !== undefined ? 'bg-action05' : 'bg-action03'}`}
    >
      <Heading title={badge || ''} className={STYLES.INDEX.PLAN_PICKER.PLAN.MOST_POPULAR} />
    </div>
  );
  const sectionsView = (
    <div className={`${STYLES.INDEX.PLAN_PICKER.PLAN.HIGHLIGHTS} pt-[12px]`}>
      <div ref={scrollableContentContainer} className={STYLES.INDEX.PLAN_PICKER.PLAN.HIGHLIGHTS_CONTENT}>
        <div ref={scrollableContent} className='w-full max-w-full overflow-y-scroll flex flex-col py-[30px] pr-[30px]'>
          {sections.map((section, index) => (
            <LiliPlanHighlightSectionView section={section} id={index} />
          ))}
        </div>
      </div>
    </div>
  );
  return (
    <div className={STYLES.INDEX.PLAN_PICKER.PLAN.LAYOUT}>
      {!isPremium && planBadge}
      <div className={STYLES.INDEX.PLAN_PICKER.PLAN.CONTENT}>
        {isPremium ? (
          <PremiumPlanInfoView
            plan={plan}
            onUpgradeClick={handlePlanUpgradeClick}
            isLoading={context.isLoading || isLoadingUploadFile}
            disabled={planUpgradePermission !== FeatureAccess.READ_WRITE}
            isAnnual={isAnnual}
          />
        ) : (
          <PlanInfoView
            plan={plan}
            onUpgradeClick={handlePlanUpgradeClick}
            isLoading={context.isLoading || isLoadingUploadFile}
            disabled={planUpgradePermission !== FeatureAccess.READ_WRITE}
            isAnnual={isAnnual}
          />
        )}
        {!isPremium && <div className={STYLES.INDEX.PLAN_PICKER.PLAN.DIVIDER} />}
        {sectionsView}
      </div>
      {shouldShowAuthUserUpgradeDisclaimer && (
        <UserMessage onClick={() => setShouldShowAuthUserUpgradeDisclaimer(false)} id=''>
          <div className='flex flex-col items-center justify-center gap-[24px] w-[450px] px-[50px] text-center py-[32px] rounded-lg bg-bw00 shadow-light'>
            <FailedIcon />
            <Heading
              title={t('ACCOUNTANT_PLAN_UPGRADE.HEADER')}
              className='font-metricRSemibold text-[30px] text-bw08'
            />
            <Heading
              title={t('ACCOUNTANT_PLAN_UPGRADE.SUB_HEADER')}
              className='font-metricRMedium text-[18px] text-bw07'
            />
            <LiliButton
              onClick={() => setShouldShowAuthUserUpgradeDisclaimer(false)}
              className='w-[85%]'
              id=''
              title='Close'
            />
          </div>
        </UserMessage>
      )}
    </div>
  );
};
